import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  IconButton
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

function formatCurrency(value) {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
}

function SyncLogs({ embedded = false }) {
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [expandedTrades, setExpandedTrades] = useState([]);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const { user } = useAuth();

  const userId = user?.id;


  useEffect(() => {
    if (!userId) {
      setError('User not authenticated. Please log in again.');
      setLoading(false);
      return;
    }
    fetchSyncLogs();
    // Auto-refresh more frequently if syncing (only for manual syncs, not scheduled)
    const interval = setInterval(() => {
      if (syncing && userId) {
        fetchSyncLogs();
      }
    }, 2000); // Refresh every 2 seconds when syncing
    return () => clearInterval(interval);
  }, [syncing]);

  const fetchSyncLogs = async () => {
    try {
      const response = await apiRequest(`/api/sync/logs?userId=${userId}&limit=50`);
      const data = response.data || [];
      setSyncLogs(data);
      // Check if any sync is in progress (only count manual syncs, not scheduled)
      const inProgress = data.some(log => log.status === 'in_progress' && log.sync_type !== 'scheduled');
      setSyncing(inProgress);
    } catch (err) {
      console.error('[SyncLogs] Failed to fetch sync logs:', err);
      console.error('[SyncLogs] Error details:', err.response?.data, err.message);
      setError('Failed to fetch sync logs');
    } finally {
      setLoading(false);
    }
  };

  const startSync = async () => {
    try {
      setSyncing(true);
      await apiRequest('/api/sync/start', { method: 'POST', body: JSON.stringify({ userId, syncType: 'manual', daysBack: 7 }) });
      fetchSyncLogs();
    } catch (err) {
      console.error('[SyncLogs] Failed to start sync:', err);
      console.error('[SyncLogs] Error details:', err.response?.data, err.message);
      setError('Failed to start sync');
      setSyncing(false);
    }
  };

  const handleRowClick = async (logId) => {
    if (expandedLogId === logId) {
      setExpandedLogId(null);
      setExpandedTrades([]);
      return;
    }

    setExpandedLogId(logId);
    setLoadingTrades(true);
    setExpandedTrades([]);

    try {
      const response = await apiRequest(`/api/sync/logs/${logId}/trades`);
      setExpandedTrades(response.data || []);
    } catch (err) {
      console.error('[SyncLogs] Failed to fetch trades:', err);
      setError('Failed to fetch trades');
    } finally {
      setLoadingTrades(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'MMM d, yyyy HH:mm');
    } catch (err) {
      console.error('[SyncLogs] Failed to format date:', dateString, err);
      return '-';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={embedded ? false : 'xl'} sx={embedded ? {} : { mt: { xs: 1, sm: 4 }, mb: 4, px: { xs: 1, sm: 3 } }}>
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box />
          <Button
            variant="contained"
            onClick={startSync}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={20} /> : null}
          >
            {syncing ? 'Syncing...' : 'Start Sync'}
          </Button>
        </Box>
      )}
      {embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={startSync}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={16} /> : null}
          >
            {syncing ? 'Syncing...' : 'Start Sync'}
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {syncLogs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No sync logs found. Start a sync to begin.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={embedded ? { maxHeight: 350, overflowY: 'auto' } : {}}>
              <Table stickyHeader={embedded}>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Accounts Synced</TableCell>
                    <TableCell>Accounts Failed</TableCell>
                    <TableCell>Trades Imported</TableCell>
                    <TableCell>Open</TableCell>
                    <TableCell>Closed</TableCell>
                    <TableCell>Dividends</TableCell>
                    <TableCell>Duplicates</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {log.new_trade_ids && log.new_trade_ids.length > 0 && (
                              <IconButton size="small" onClick={() => handleRowClick(log.id)}>
                                {expandedLogId === log.id ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            )}
                            {formatDateTime(log.start_time)}
                          </Box>
                        </TableCell>
                        <TableCell>{log.sync_type}</TableCell>
                        <TableCell>
                          <Chip label={log.status} color={getStatusColor(log.status)} size="small" />
                        </TableCell>
                        <TableCell>{formatDuration(log.duration)}</TableCell>
                        <TableCell>{log.accounts_synced}</TableCell>
                        <TableCell>{log.accounts_failed}</TableCell>
                        <TableCell>{log.trades_imported}</TableCell>
                        <TableCell>{log.trades_open}</TableCell>
                        <TableCell>{log.trades_closed}</TableCell>
                        <TableCell>{log.dividends_imported}</TableCell>
                        <TableCell>{log.duplicates_found}</TableCell>
                        <TableCell>
                          {log.errors ? (
                            <Button size="small" onClick={() => alert(log.errors)}>
                              View
                            </Button>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                      {log.new_trade_ids && log.new_trade_ids.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={12} sx={{ p: 0, border: 'none' }}>
                            <Collapse in={expandedLogId === log.id}>
                              <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.08)' }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, color: 'success.main', fontWeight: 'bold' }}>
                                  New Transactions ({log.trades_imported})
                                </Typography>
                                {loadingTrades ? (
                                  <CircularProgress size={24} />
                                ) : expandedTrades.length > 0 ? (
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Symbol</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Price/Dividend</TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}>Account</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {expandedTrades.map((trade) => (
                                          <TableRow key={trade.id} sx={{ bgcolor: 'rgba(76, 175, 80, 0.12)' }}>
                                            <TableCell sx={{ fontWeight: 'medium' }}>{trade.symbol}</TableCell>
                                            <TableCell>{trade.trade_type}</TableCell>
                                            <TableCell>{trade.trade_date}</TableCell>
                                            <TableCell>{trade.quantity}</TableCell>
                                            <TableCell>
                                              {trade.trade_type === 'DIVIDEND' || trade.trade_type === 'dividend' 
                                                ? formatCurrency(trade.dividend)
                                                : formatCurrency(trade.price)}
                                            </TableCell>
                                            <TableCell>{trade.account}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No new transactions found
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default SyncLogs;
